import { DOCUMENT } from '@angular/common';
import { EventEmitter, Injectable, computed, inject, signal } from '@angular/core';
import { Direction, Directionality } from '@angular/cdk/bidi';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export type AppLang = 'en' | 'ar';

/**
 * THE single owner of language and direction.
 *
 * Before the kit, four places raced to set this: `AppComponent`
 * (`updateLanguageSettings`), `NavComponent` (`setDomDirection` + a `currentDir`
 * bound with `[dir]`), the auth components, and `shared-table`, which read
 * `localStorage.lang` once at construction and never updated. Switching the
 * language therefore left parts of the UI in the previous direction until F5.
 *
 * Everything now flows through here:
 *   - `init()` is called from an APP_INITIALIZER and AWAITS the first
 *     `translate.use(lang)` before the first paint (BIZ-02: `setDefaultLang`
 *     only sets a fallback, so the login screen used to flash raw AUTH.* keys);
 *   - `<html lang dir>` is written here and nowhere else;
 *   - the service is provided as the CDK `Directionality`, so every CDK
 *     overlay (select, tooltip, sheet, dialog, datepicker) lands on the
 *     correct side without a single `[dir]` binding in a template.
 *
 * `lang`, `dir` and `isRtl` are signals; `change` is the CDK Directionality
 * stream. The persisted key stays `lang` in `localStorage` — the same key the
 * old code used, so a signed-in vendor keeps their choice across this upgrade.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService implements Directionality {
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private readonly _lang = signal<AppLang>(readStoredLang());

  readonly lang = this._lang.asReadonly();
  readonly dir = computed<Direction>(() => (this._lang() === 'ar' ? 'rtl' : 'ltr'));
  readonly isRtl = computed(() => this._lang() === 'ar');

  /** CDK Directionality contract. */
  get value(): Direction {
    return this.dir();
  }
  /** CDK Directionality contract: an EventEmitter, not a plain Subject. */
  readonly change = new EventEmitter<Direction>();

  /**
   * Called by APP_INITIALIZER. Activates the stored language and RESOLVES ONLY
   * WHEN THE FILE IS LOADED, so nothing paints against missing keys.
   *
   * `setDefaultLang('en')` is kept exactly as the app had it: the default is
   * the FALLBACK used when a key is missing from the active file. It was 'ar'
   * while the app starts in 'en', so any key absent from en.json silently
   * rendered Arabic inside the English UI (BIZ-02).
   */
  async init(): Promise<void> {
    const lang = this._lang();
    // The old AppComponent.updateLanguageSettings() wrote the resolved language
    // back on every run, so a first visit persisted 'en'. Kept identical.
    try {
      localStorage.setItem('lang', lang);
    } catch {
      /* private mode */
    }
    this.applyDom(lang);
    this.translate.setDefaultLang('en');
    await firstValueFrom(this.translate.use(lang)).catch(() => undefined);
  }

  /** Switch language: persist, activate, and update <html> + Directionality. */
  use(lang: AppLang): void {
    if (lang !== 'ar' && lang !== 'en') {
      return;
    }
    this._lang.set(lang);
    try {
      localStorage.setItem('lang', lang);
    } catch {
      /* private mode — the in-memory signal is still correct */
    }
    this.applyDom(lang);
    this.translate.use(lang);
    this.change.emit(this.dir());

    // `LOCALE_ID` is resolved ONCE, at bootstrap, from the key just written, so
    // it cannot follow an in-place switch. Without this reload the strings and
    // the direction change while every `| date` keeps the previous language for
    // the rest of the session — Arabic copy around `Sep 4, 2026`. The customer
    // web reloads here for the same reason.
    //
    // Safe to do from `use()`: `init()` does not call it, so there is no boot
    // loop, and the language is persisted above before the page goes away. The
    // access token lives in memory, so the reload runs the normal
    // `bootstrapSession()` restore from the refresh cookie.
    try {
      window.location.reload();
    } catch {
      /* no window (SSR / tests) — the signal above is already correct */
    }
  }

  /** The other language — what a one-button toggle switches to. */
  get other(): AppLang {
    return this._lang() === 'ar' ? 'en' : 'ar';
  }

  toggle(): void {
    this.use(this.other);
  }

  private applyDom(lang: AppLang): void {
    const dir: Direction = lang === 'ar' ? 'rtl' : 'ltr';
    const html = this.document?.documentElement;
    if (html) {
      html.setAttribute('lang', lang);
      html.setAttribute('dir', dir);
    }
    if (this.document?.body) {
      this.document.body.setAttribute('dir', dir);
    }
  }

  ngOnDestroy(): void {
    this.change.complete();
  }
}

/**
 * The ONE reader of the stored language. Exported because `main.ts` needs the
 * same answer to seed `LOCALE_ID` at bootstrap, and two places reading the
 * storage key their own way is how the copy and the dates end up disagreeing.
 */
export function readStoredLang(): AppLang {
  try {
    return (localStorage.getItem('lang') as AppLang) === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}
